import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOHead } from '@/components/SEOHead';
import axios from 'axios';
import { FileText, Upload, Check, AlertCircle, Loader2, Download, CreditCard, Building, Lock, Printer } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ALLOWED = ['image/jpeg', 'image/png', 'application/pdf'];

const statusColors = {
  draft: 'bg-slate-100 text-slate-600', sent: 'bg-blue-50 text-blue-600', viewed: 'bg-purple-50 text-purple-600',
  pending_verification: 'bg-amber-50 text-amber-600', paid: 'bg-green-50 text-green-600', overdue: 'bg-red-50 text-red-600',
};

export default function InvoicePage() {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payMethod, setPayMethod] = useState('wire');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploaded, setUploaded] = useState(false);
  const [payingOnline, setPayingOnline] = useState(false);

  useEffect(() => {
    axios.get(`${API}/invoices/${invoiceId}`)
      .then(r => {
        setInvoice(r.data);
        // Check if returning from PayMongo payment
        if (searchParams.get('paid') === '1' && r.data.status !== 'paid') {
          setInvoice(prev => prev ? { ...prev, status: 'paid' } : prev);
        }
      })
      .catch(() => setError('Invoice not found.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const handlePayOnline = async () => {
    setPayingOnline(true);
    try {
      const resp = await axios.post(`${API}/invoices/${invoiceId}/pay-online`);
      window.location.href = resp.data.checkout_url;
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Payment setup failed.');
      setPayingOnline(false);
    }
  };

  const handleUpload = async (files) => {
    const file = files[0];
    if (!file) return;
    if (!ALLOWED.includes(file.type)) { setUploadError('Only JPG, PNG, and PDF accepted.'); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError('Max 5MB.'); return; }
    setUploading(true); setUploadError('');
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API}/invoices/${invoiceId}/upload`, form);
      setUploaded(true);
      setInvoice(prev => prev ? { ...prev, status: 'pending_verification' } : prev);
    } catch (err) { setUploadError(err.response?.data?.detail || 'Upload failed.'); }
    finally { setUploading(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-8 h-8 text-brand-blue animate-spin" /></div>;
  if (error || !invoice) return <div className="min-h-screen flex items-center justify-center bg-white"><div className="text-center"><h2 className="text-xl font-bold text-slate-900 mb-2">Invoice Not Found</h2><Link to="/" className="text-brand-blue text-sm font-medium">Go to MediaLabs</Link></div></div>;

  const sym = invoice.currency === 'PHP' ? '₱' : '$';
  const isPaid = invoice.status === 'paid';
  const isPending = invoice.status === 'pending_verification';

  return (
    <div className="min-h-screen bg-slate-50 py-8 sm:py-12 px-4" data-testid="invoice-page">
      <SEOHead title={`Invoice ${invoice.invoice_id} | MediaLabs`} path={`/invoice/${invoiceId}`} />
      <div className="max-w-2xl mx-auto">
        {/* Download PDF button */}
        <div className="flex justify-end mb-3 print:hidden">
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors shadow-sm" data-testid="download-pdf">
            <Printer className="w-3.5 h-3.5" /> Download PDF
          </button>
        </div>
        {/* Invoice Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden print:shadow-none print:border-0">
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-blue to-brand-blue-dark p-6 sm:p-8 text-white">
            <div className="flex items-center justify-between">
              <div><img src="/medialabs-logo.jpg" alt="MediaLabs" className="h-10 brightness-0 invert" /></div>
              <div className="text-right">
                <p className="text-xs opacity-70 uppercase tracking-wider">Invoice</p>
                <p className="text-lg font-bold">{invoice.invoice_id}</p>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            {/* Status + Dates */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider ${statusColors[invoice.status] || statusColors.draft}`}>{invoice.status.replace('_', ' ')}</span>
              <div className="flex gap-4 text-xs text-slate-500">
                <span>Issued: <strong className="text-slate-700">{invoice.issue_date}</strong></span>
                <span>Due: <strong className="text-slate-700">{invoice.due_date}</strong></span>
              </div>
            </div>

            {/* Bill To */}
            <div className="mb-6 p-4 bg-slate-50 rounded-xl">
              <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">Bill To</p>
              <p className="text-sm font-bold text-slate-900">{invoice.client_name}</p>
              {invoice.company && <p className="text-xs text-slate-600">{invoice.company}</p>}
              <p className="text-xs text-slate-500">{invoice.client_email}</p>
            </div>

            {/* Line Items */}
            <table className="w-full text-sm mb-6">
              <thead><tr className="border-b border-slate-200">
                <th className="text-left py-2 text-xs font-semibold text-slate-400 uppercase">Description</th>
                <th className="text-center py-2 text-xs font-semibold text-slate-400 uppercase w-16">Qty</th>
                <th className="text-right py-2 text-xs font-semibold text-slate-400 uppercase w-24">Price</th>
                <th className="text-right py-2 text-xs font-semibold text-slate-400 uppercase w-24">Total</th>
              </tr></thead>
              <tbody>
                {(invoice.line_items || []).map((item, i) => (
                  <tr key={`line-${i}`} className="border-b border-slate-100">
                    <td className="py-3 text-slate-800">{item.description}</td>
                    <td className="py-3 text-center text-slate-600">{item.qty}</td>
                    <td className="py-3 text-right text-slate-600">{sym}{(item.price || 0).toLocaleString()}</td>
                    <td className="py-3 text-right font-semibold text-slate-800">{sym}{((item.qty || 1) * (item.price || 0)).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end mb-6">
              <div className="w-56 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="text-slate-700">{sym}{(invoice.subtotal || 0).toLocaleString()}</span></div>
                {invoice.tax > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">Tax ({invoice.tax_rate}%)</span><span className="text-slate-700">{sym}{(invoice.tax || 0).toLocaleString()}</span></div>}
                <div className="flex justify-between text-base font-bold border-t border-slate-200 pt-2"><span className="text-slate-900">Total</span><span className="text-brand-orange">{sym}{(invoice.total || 0).toLocaleString()}</span></div>
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div className="p-4 bg-slate-50 rounded-xl mb-6">
                <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">Payment Instructions</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{invoice.notes}</p>
              </div>
            )}

            {/* Payment Zone (only if not paid) */}
            {!isPaid && (
              <div className="border-t border-slate-200 pt-6 print:hidden">
                <h3 className="text-sm font-bold text-slate-900 mb-4">Submit Payment</h3>

                {isPending || uploaded ? (
                  <div className="text-center py-6 bg-green-50 rounded-xl border border-green-200">
                    <Check className="w-10 h-10 text-green-500 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-green-700">Document Received</p>
                    <p className="text-xs text-green-600 mt-1">Your invoice will be marked as Paid once the funds clear our account.</p>
                  </div>
                ) : (
                  <>
                    {/* Pay Online Button */}
                    <button onClick={handlePayOnline} disabled={payingOnline}
                      className="w-full py-3.5 rounded-xl bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white text-sm font-bold shadow-lg shadow-brand-orange/20 hover:shadow-xl transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2 mb-4"
                      data-testid="pay-online-btn">
                      {payingOnline ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing checkout...</> : <><Lock className="w-4 h-4" /> Pay Online Securely — {sym}{(invoice.total || 0).toLocaleString()}</>}
                    </button>
                    <p className="text-[10px] text-slate-400 text-center mb-5">Accepts Visa, Mastercard, GCash, Maya via PayMongo</p>

                    <div className="flex items-center gap-3 mb-5"><div className="flex-1 h-px bg-slate-200" /><span className="text-[10px] text-slate-400 uppercase tracking-wider">or pay manually</span><div className="flex-1 h-px bg-slate-200" /></div>

                    <div className="flex gap-2 mb-4">
                      {[{ val: 'wire', label: 'Wire Transfer', icon: Building }, { val: 'check', label: 'Check Deposit', icon: CreditCard }].map(m => (
                        <button key={m.val} onClick={() => setPayMethod(m.val)} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold transition-all ${payMethod === m.val ? 'bg-brand-blue text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                          <m.icon className="w-4 h-4" /> {m.label}
                        </button>
                      ))}
                    </div>

                    <p className="text-xs text-slate-500 mb-3">Upload your deposit slip or a photo of the {payMethod === 'wire' ? 'wire transfer confirmation' : 'check'} below.</p>

                    {uploadError && <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-50 border border-red-100 mb-3 text-xs text-red-600"><AlertCircle className="w-3.5 h-3.5 shrink-0" />{uploadError}</div>}

                    <div
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => { e.preventDefault(); handleUpload(Array.from(e.dataTransfer.files)); }}
                      onClick={() => document.getElementById('proof-input')?.click()}
                      className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-brand-blue/30 transition-colors"
                    >
                      <input id="proof-input" type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={e => handleUpload(Array.from(e.target.files || []))} />
                      {uploading ? <Loader2 className="w-6 h-6 text-brand-blue animate-spin mx-auto" /> : <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />}
                      <p className="text-xs text-slate-500">{uploading ? 'Uploading...' : 'Drag & drop or click to browse'}</p>
                      <p className="text-[10px] text-slate-400 mt-1">JPG, PNG, PDF • Max 5MB</p>
                    </div>
                  </>
                )}
              </div>
            )}

            {isPaid && (
              <div className="text-center py-6 bg-green-50 rounded-xl border border-green-200 print:hidden">
                <Check className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-green-700">Invoice Paid</p>
                <p className="text-xs text-green-600 mt-1">Thank you for your payment.</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 sm:px-8 py-4 bg-slate-50 border-t border-slate-100 text-center">
            <p className="text-[10px] text-slate-400">MediaLabs I.T. Solutions • medialabs.ph</p>
          </div>
        </div>
      </div>
    </div>
  );
}
